import React, { useEffect, useState } from "react";
import MaterialIcon from "../../../components/MaterialIcon";
import { listTeamsAccounts } from "../../../helper/teamsApi";
import { TEAMS_ACCOUNT_EVENT, teamsViewState } from "../../../utils/teams/teamsConnect";
import { Card, Skeleton } from "./CockpitPrimitives";

const isErrorStatus = (status) => String(status || "").toLowerCase().includes("error");

/**
 * GET /teams/accounts → { visible, state } (null while loading or when the list failed).
 * Visible once the server enables Teams for the user or while an account exists.
 */
function useTeamsChannel(enabled, refreshKey) {
  const [teams, setTeams] = useState(null);
  useEffect(() => {
    if (!enabled) {
      setTeams(null);
      return undefined;
    }
    let alive = true;
    const load = () =>
      listTeamsAccounts()
        .then((res) => {
          if (!alive || !res || !res.ok) return;
          const account = (Array.isArray(res.accounts) && res.accounts[0]) || null;
          const { state, archived } = teamsViewState(account);
          setTeams({ visible: !!res.enabled || state !== "none" || archived, state });
        })
        .catch(() => {});
    load();
    // Import progress / reconnects arrive over the socket.
    window.addEventListener(TEAMS_ACCOUNT_EVENT, load);
    return () => {
      alive = false;
      window.removeEventListener(TEAMS_ACCOUNT_EVENT, load);
    };
  }, [enabled, refreshKey]);
  return teams;
}

function Channel({ icon, label, status, tone, cta, onClick, loading }) {
  return (
    <button type="button" className={`cockpit-channel cockpit-channel--${tone}`} onClick={onClick}>
      <span className="cockpit-channel-icon">
        <MaterialIcon icon={icon} size={18} color="currentColor" weight={400} />
      </span>
      <span className="cockpit-channel-text">
        <span className="cockpit-channel-name">{label}</span>
        {loading ? (
          <Skeleton width={90} height={10} />
        ) : (
          <span className="cockpit-channel-status">
            <span className="cockpit-channel-dot" />
            <span className="cockpit-channel-status-text">{status}</span>
          </span>
        )}
      </span>
      {cta && !loading ? (
        <span className="cockpit-channel-cta">
          <span>{cta}</span>
          <MaterialIcon icon="arrow_forward" size={14} color="currentColor" weight={400} />
        </span>
      ) : (
        <MaterialIcon icon="chevron_right" size={18} color="#a7aeba" />
      )}
    </button>
  );
}

/**
 * "Kanäle": Kjuup-Chat, E-Mail, Weiterleitungsadresse and Microsoft Teams with
 * their state. A channel that is off or not connected links to the settings
 * where it is switched on or connected; a connected one to where it is managed.
 *   chat   { on }                           (always shown)
 *   email  { visible, accounts, loading }   (Kjuup.io; accounts with provider "forward" feed the forwarding tile)
 *   teamsAllowed                            Kjuup.io: Teams shows once the server enables it
 *   onOpen(key)                             'chat' | 'email' | 'forward' | 'teams'
 */
export default function CockpitChannels({ strings, chat, email, teamsAllowed, refreshKey, onOpen }) {
  const teams = useTeamsChannel(!!teamsAllowed, refreshKey);
  const t = (key, fallback) => strings[key] || fallback;
  const connected = t("cockpit_channel_connected", "Verbunden");
  const notConnected = t("cockpit_channel_not_connected", "Nicht verbunden");
  const connect = t("cockpit_channel_connect", "Verbinden");

  const channels = [];

  channels.push({
    key: "chat",
    icon: "forum",
    label: t("cockpit_channel_chat", "Kjuup-Chat"),
    tone: chat.on ? "on" : "off",
    status: chat.on ? t("cockpit_channel_active", "Aktiv") : t("cockpit_channel_hidden", "Ausgeblendet"),
    cta: chat.on ? null : t("cockpit_channel_enable", "Aktivieren"),
  });

  if (email && email.visible) {
    const all = Array.isArray(email.accounts) ? email.accounts : [];
    // The Kjuup forwarding address gets its own tile; "E-Mail" is the connected mailboxes.
    const accounts = all.filter((a) => a.provider !== "forward");
    const forward = all.find((a) => a.provider === "forward") || null;
    const failing = accounts.some((a) => isErrorStatus(a.status));
    const first = accounts[0] && (accounts[0].email || accounts[0].displayName);
    const more = accounts.length > 1 ? ` +${accounts.length - 1}` : "";
    channels.push({
      key: "email",
      icon: "mail",
      label: t("cockpit_channel_email", "E-Mail"),
      loading: email.loading,
      tone: failing ? "warn" : accounts.length ? "on" : "off",
      status: failing
        ? t("cockpit_channel_check", "Verbindung prüfen")
        : accounts.length
          ? `${first || connected}${more}`
          : notConnected,
      cta: failing ? t("cockpit_channel_fix", "Prüfen") : accounts.length ? null : connect,
    });

    const forwardFailing = !!forward && isErrorStatus(forward.status);
    channels.push({
      key: "forward",
      icon: "forward_to_inbox",
      label: t("cockpit_channel_forward", "Weiterleitungsadresse"),
      loading: email.loading,
      tone: forwardFailing ? "warn" : forward ? "on" : "off",
      status: forwardFailing
        ? t("cockpit_channel_check", "Verbindung prüfen")
        : forward
          ? t("cockpit_channel_active", "Aktiv")
          : t("cockpit_channel_not_set_up", "Nicht eingerichtet"),
      cta: forwardFailing ? t("cockpit_channel_fix", "Prüfen") : forward ? null : t("cockpit_channel_set_up", "Einrichten"),
    });
  }

  if (teamsAllowed && (teams == null || teams.visible)) {
    const state = teams ? teams.state : null;
    const linked = state === "connected" || state === "importing" || state === "connecting";
    const broken = state === "reconnect" || state === "consent" || state === "policy";
    channels.push({
      key: "teams",
      icon: "groups",
      label: t("cockpit_channel_teams", "Microsoft Teams"),
      loading: teams == null,
      tone: broken ? "warn" : linked ? "on" : "off",
      status: broken
        ? t("cockpit_channel_broken", "Verbindung unterbrochen")
        : state === "connected"
          ? connected
          : linked
            ? t("cockpit_channel_importing", "Import läuft")
            : state === "removing"
              ? t("cockpit_channel_removing", "Wird getrennt")
              : notConnected,
      cta: broken ? t("cockpit_channel_reconnect", "Neu verbinden") : linked || state === "removing" ? null : connect,
    });
  }

  return (
    <Card className="cockpit-channels">
      <div className="cockpit-channels-label">{t("cockpit_channels_title", "Kanäle")}</div>
      <div className="cockpit-channels-list" style={{ "--cockpit-channels-n": channels.length }}>
        {channels.map(({ key, ...channel }) => (
          <Channel key={key} {...channel} onClick={() => onOpen(key)} />
        ))}
      </div>
    </Card>
  );
}
